import { useEffect } from 'react';
import {
    Admin,
    CustomRoutes,
    ListGuesser,
    RaThemeOptions,
    Resource,
    defaultTheme,
    localStorageStore,
} from 'react-admin';
import type { AdminProps, AuthProvider, DataProvider } from 'react-admin';
import { deepmerge } from '@mui/utils';
import { Route } from 'react-router';
import { SetPasswordPage } from 'ra-supabase';
import { CustomForgotPasswordPage } from '../login/CustomForgotPasswordPage';
import carriers from '../carriers';
import { Layout } from '../layout/Layout';
import { i18nProvider } from './i18nProvider';
import accounts from '../accounts';
// import hailPricingTool from '../hail-pricing-tool';
import contacts from '../contacts';
import { DashboardRedirector } from '../dashboard/DashboardRedirector';
import quotes from '../quotes';
import { LoginPage } from '../login/LoginPage';
import { SignupPage } from '../login/SignupPage';
import { RegistrationPage } from '../login/RegistrationPage';
import {
    authProvider as defaultAuthProvider,
    dataProvider as defaultDataProvider,
} from '../providers/supabase';
import sales from '../sales';
import { MfaVerifyPage, MfaEnrollPage } from '../mfa';
import { SettingsPage } from '../settings/SettingsPage';
import {
    ConfigurationContextValue,
    ConfigurationProvider,
} from './ConfigurationContext';
import { SiteStatusProvider } from './SiteStatusContext';
import {
    defaultAccountSectors,
    defaultContactGender,
    defaultQuoteCategories,
    defaultQuotePipelineStatuses,
    defaultQuoteStages,
    defaultLogo,
    defaultNoteStatuses,
    defaultTaskTypes,
    defaultTitle,
} from './defaultConfiguration';
import RateManagementPage from '../carriers/RateManagementPage';
import { HailDocsPage } from '../hail-docs';
import { HailPricingToolPage } from '../hail-pricing-tool/HailPricingToolPage';
import { AdminPanelPage } from '../admin/AdminPanelPage';
import { QuoteShowPage } from '../quotes/QuoteShowPage';
import TestLogoPdf from '../hail-pricing-tool/TestLogoPdf'; // Add this import
import TestPDFPreview from '../hail-pricing-tool/TestPDFPreview';
import { SupportPage } from '../support/SupportPage';

// Define the interface for the CRM component props
export type CRMProps = {
    dataProvider?: DataProvider;
    authProvider?: AuthProvider;
    lightTheme?: RaThemeOptions;
    darkTheme?: RaThemeOptions;
} & Partial<ConfigurationContextValue> &
    Partial<AdminProps>;

// cropAMS Design System Colors
const emerald = {
    50: '#ecfdf5',
    100: '#d1fae5',
    200: '#a7f3d0',
    500: '#10b981',
    600: '#059669',
    700: '#047857',
};

const slate = {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
};

export const defaultLightTheme = deepmerge(defaultTheme, {
    palette: {
        background: {
            default: slate[50], // Page background — bg-slate-50
            paper: '#ffffff',
            lightGreen: emerald[100], // Accent background
        },
        primary: {
            light: emerald[200],
            main: emerald[500], // THE brand color — emerald-500
            dark: emerald[700],
            contrastText: '#ffffff',
        },
        secondary: {
            light: slate[100],
            main: slate[200],
            dark: slate[400],
            contrastText: '#000000',
        },
        grey: {
            50: slate[50],
            100: slate[100],
            200: slate[200],
            300: slate[300],
            400: slate[400],
            500: slate[500],
            600: slate[600],
            700: slate[700],
            800: slate[800],
            900: slate[900],
        },
    },
    typography: {
        fontFamily:
            'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        h6: {
            fontWeight: 600,
        },
    },
    shape: {
        borderRadius: 8, // 0.5rem — cropAMS default radius
    },
    components: {
        MuiAppBar: {
            styleOverrides: {
                colorDefault: {
                    backgroundColor: '#ffffff',
                },
                colorPrimary: {
                    backgroundColor: '#ffffff',
                    color: slate[900],
                    boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)', // shadow-sm
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    border: `1px solid ${slate[200]}`,
                },
                elevation1: {
                    boxShadow: 'none',
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    border: `1px solid ${slate[200]}`,
                    boxShadow: 'none',
                    borderRadius: 8,
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                root: {
                    minHeight: '48px',
                },
                indicator: {
                    height: '3px',
                    backgroundColor: emerald[500], // Emerald indicator
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: 500,
                    minHeight: '48px',
                    color: slate[600],
                    '&.Mui-selected': {
                        color: emerald[700], // Emerald selected tab
                    },
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                containedPrimary: {
                    backgroundColor: emerald[500],
                    '&:hover': {
                        backgroundColor: emerald[600],
                    },
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                colorPrimary: {
                    backgroundColor: emerald[50],
                    color: emerald[700],
                },
            },
        },
        RaFileInput: {
            styleOverrides: {
                root: {
                    '& .RaFileInput-dropZone': {
                        backgroundColor: slate[100],
                        border: `2px dashed ${slate[400]}`,
                        borderRadius: 8,
                    },
                },
            },
        },
    },
});

/**
 * CRM Component
 *
 * This component sets up and renders the main CRM application using `react-admin`. It provides
 * default configurations and themes but allows for customization through props. The component
 * wraps the application with a `ConfigurationProvider` to provide configuration values via context.
 *
 * @param {Array<ContactGender>} contactGender - The gender options for contacts used in the application.
 * @param {string[]} accountSectors - The list of account sectors used in the application.
 * @param {RaThemeOptions} darkTheme - The theme to use when the application is in dark mode.
 * @param {string[]} quoteCategories - The categories of quotes used in the application.
 * @param {string[]} quotePipelineStatuses - The statuses of quotes in the pipeline used in the application.
 * @param {QuoteStage[]} quoteStages - The stages of quotes used in the application.
 * @param {RaThemeOptions} lightTheme - The theme to use when the application is in light mode.
 * @param {string} logo - The logo used in the CRM application.
 * @param {NoteStatus[]} noteStatuses - The statuses of notes used in the application.
 * @param {string[]} taskTypes - The types of tasks used in the application.
 * @param {string} title - The title of the CRM application.
 *
 * @returns {JSX.Element} The rendered CRM application.
 *
 * @example
 * // Basic usage of the CRM component
 * import { CRM } from './CRM';
 *
 * const App = () => (
 *     <CRM
 *         logo="/path/to/logo.png"
 *         title="My Custom CRM"
 *         lightTheme={{
 *             ...defaultTheme,
 *             palette: {
 *                 primary: { main: '#0000ff' },
 *             },
 *         }}
 *     />
 * );
 *
 * export default App;
 */
export const CRM = ({
    contactGender = defaultContactGender,
    accountSectors = defaultAccountSectors,
    darkTheme,
    quoteCategories = defaultQuoteCategories,
    quotePipelineStatuses = defaultQuotePipelineStatuses,
    quoteStages = defaultQuoteStages,
    lightTheme = defaultLightTheme, // TODO: remove this
    logo = defaultLogo,
    noteStatuses = defaultNoteStatuses,
    taskTypes = defaultTaskTypes,
    title = defaultTitle,
    dataProvider = defaultDataProvider,
    authProvider = defaultAuthProvider,
    disableTelemetry,
    ...rest
}: CRMProps) => {
    useEffect(() => {
        if (
            disableTelemetry ||
            process.env.NODE_ENV !== 'production' ||
            typeof window === 'undefined' ||
            typeof window.location === 'undefined' ||
            typeof Image === 'undefined'
        ) {
            return;
        }
        const img = new Image();
        img.src = `https://atomic-crm-telemetry.marmelab.com/atomic-crm-telemetry?domain=${window.location.hostname}`;
    }, [disableTelemetry]);

    return (
        <SiteStatusProvider>
            <ConfigurationProvider
                contactGender={contactGender}
                accountSectors={accountSectors}
                quoteCategories={quoteCategories}
                quotePipelineStatuses={quotePipelineStatuses}
                quoteStages={quoteStages}
                logo={logo}
                noteStatuses={noteStatuses}
                taskTypes={taskTypes}
                title={title}
            >
                <Admin
                    dataProvider={dataProvider}
                    authProvider={authProvider}
                    store={localStorageStore(undefined, 'CRM')}
                    layout={Layout}
                    loginPage={LoginPage}
                    dashboard={DashboardRedirector}
                    theme={lightTheme}
                    darkTheme={darkTheme || null}
                    i18nProvider={i18nProvider}
                    requireAuth
                    disableTelemetry
                    {...rest}
                >
                    <CustomRoutes noLayout>
                        <Route path="/test-logo" element={<TestLogoPdf />} />
                        <Route path="/test-pdf" element={<TestPDFPreview />} />
                        <Route
                            path={SignupPage.path}
                            element={<SignupPage />}
                        />
                        <Route
                            path={RegistrationPage.path}
                            element={<RegistrationPage />}
                        />
                        <Route
                            path={SetPasswordPage.path}
                            element={<SetPasswordPage />}
                        />
                        <Route
                            path={CustomForgotPasswordPage.path}
                            element={<CustomForgotPasswordPage />}
                        />
                        <Route
                            path={MfaVerifyPage.path}
                            element={<MfaVerifyPage />}
                        />
                        <Route
                            path={MfaEnrollPage.path}
                            element={<MfaEnrollPage />}
                        />
                    </CustomRoutes>

                    <CustomRoutes>
                        <Route
                            path={SettingsPage.path}
                            element={<SettingsPage />}
                        />
                        <Route
                            path="/hail-pricing-tool"
                            element={<HailPricingToolPage />}
                        />
                        <Route
                            path="/rate-manager"
                            element={<RateManagementPage />}
                        />
                        <Route path="/hail-docs" element={<HailDocsPage />} />
                        <Route
                            path="/admin-panel"
                            element={<AdminPanelPage />}
                        />
                        <Route
                            path="/quotes/:id/page"
                            element={<QuoteShowPage />}
                        />
                        <Route
                            path={SupportPage.path}
                            element={<SupportPage />}
                        />
                    </CustomRoutes>
                    <Resource name="quotes" {...quotes} />
                    {/* <Resource name="hail-pricing-tool" {...hailPricingTool} /> */}
                    <Resource name="carriers" {...carriers} />
                    <Resource name="contacts" {...contacts} />
                    <Resource name="accounts" {...accounts} />
                    <Resource name="contactNotes" />
                    <Resource name="quoteNotes" />
                    <Resource name="tasks" list={ListGuesser} />
                    <Resource name="sales" {...sales} />
                    <Resource name="tags" list={ListGuesser} />
                </Admin>
            </ConfigurationProvider>
        </SiteStatusProvider>
    );
};
